/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { Typography2 } from '@coursera/cds-core';

import StatusIcon, { getStatusIconLabel } from 'bundles/course-frame/components/course-outline/StatusIcon';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';
import useCoachItemType from 'bundles/item-widget/hooks/useCoachItemType';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getFormattedTitlePrefix, getIsGradable, getIsLocked } from 'bundles/learner-progress/utils/Item';
import { useCourseAiGradingContext } from 'bundles/peer/components/ai-grading/CourseAiEligibilityProvider';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/course-frame';

const styles = {
  container: css`
    display: flex;
    border-left: 4px solid transparent;

    &:hover {
      text-decoration: none;
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  locked: css`
    background-color: var(--cds-color-interactive-background-secondary-hover-weak);

    &:hover {
      background-color: var(--cds-color-interactive-background-secondary-pressed-weak);
    }
  `,
  content: css`
    display: flex;
    width: 100%;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    gap: 12px;
  `,
  icon: css`
    align-self: center;
    height: 20px;
  `,
  selected: css`
    border-left: 4px solid var(--cds-color-interactive-primary);
  `,
};

type Props = {
  computedItem: Item;
  highlighted?: boolean;
  showDistinctAvailabilityReviewDates?: boolean;
};

/**
 * This is Work-In-Progress component to replace the existing NavSingleItemDisplay.
 * TODO:
 * - Lock tooltips (https://coursera.atlassian.net/browse/GRADE-6717)
 * - Freemium support (https://coursera.atlassian.net/browse/GRADE-6716)
 * @see https://github.com/webedx-spark/web/blob/main/static/bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay.tsx
 */
const NavSingleItemDisplay = ({ computedItem, highlighted, showDistinctAvailabilityReviewDates }: Props) => {
  const { getItemAiGradingEligibility, getItemFullyAiGradingEligibility } = useCourseAiGradingContext();
  const isAiGradingEligible =
    getItemAiGradingEligibility(computedItem.id) || getItemFullyAiGradingEligibility(computedItem.id);

  const coachItemType = useCoachItemType(computedItem);
  const { isGuidedProject } = useFluxibleStore(CourseStore, (store) => ({
    isGuidedProject: store.isGuidedProject(),
  }));

  const formattedTitlePrefix = getFormattedTitlePrefix(
    computedItem,
    isGuidedProject,
    isAiGradingEligible,
    coachItemType
  );

  const itemStatus = useMemo(() => {
    // Don't show any status info for the artificial "Review your peers" item type
    if (computedItem.contentSummary.typeName === 'splitPeerReviewItem') {
      return '';
    }

    if (!getIsGradable(computedItem)) {
      return '';
    }

    if (!computedItem.isSubmitted) {
      return '';
    }

    if (computedItem.isSubmitted && !computedItem.isGraded) {
      return _t('Results pending');
    }

    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradeFormatted = truncToPlace(grade * 100, 2);
    return _t('Grade: #{gradeFormatted}%', { gradeFormatted });
  }, [computedItem]);

  const iconLabelForLink = getStatusIconLabel(computedItem);

  const isLocked = getIsLocked(computedItem);

  const highlightedLabel = highlighted ? _t('selected link') : '';
  const ariaLabel = [highlightedLabel, formattedTitlePrefix, computedItem.name, iconLabelForLink, itemStatus]
    .filter((status) => !!status)
    .join(', ');

  return (
    <Link
      to={computedItem.resourcePath}
      css={[styles.container, highlighted && styles.selected, isLocked && styles.locked]}
      aria-label={ariaLabel}
    >
      <div css={styles.content}>
        <div css={styles.icon}>
          <StatusIcon computedItem={computedItem} />
        </div>
        <div>
          <Typography2 variant="bodySecondary" component="div" color="body">
            {computedItem.name}
          </Typography2>
          <Typography2 variant="bodySecondary" component="div" color="emphasis.neutral">
            {formattedTitlePrefix}
            {itemStatus ? ` • ${itemStatus}` : ''}
          </Typography2>
        </div>
      </div>
    </Link>
  );
};

export default NavSingleItemDisplay;
